import { IExcelReportData, ITableData } from 'types/excelExport';
import { IDispositionData } from 'views/ProFormaTable/types';
import {
    createHeaderRow,
    createDataRow,
    createBlankRow,
    createTripleValueRow,
    formatDate,
} from './helpers';
import { EXCEL_COLORS, EXCEL_NUM_FORMATS } from './constants';
import { getModelExportConfig } from './modelConfig';

interface IDispositionSheetParams {
    dealName: string;
    disposition?: IDispositionData;
    saleDate: string;
    saleMonth: number | null;
    proFormaModelId: number;
    showCents?: boolean;
    showPerGrossSf?: boolean;
    showPerRentableSf?: boolean;
    showPerUnit?: boolean;
}

const createDispositionLabelValueRow = (
    label: string,
    value: string | number | null | undefined,
    numFmt?: string,
): ITableData => {
    const isNumericFormat = Boolean(numFmt);
    const displayValue = value ?? (isNumericFormat ? 0 : '');

    return createDataRow([
        { value: label, width: 30, cellFont: { bold: true } },
        { value: displayValue, width: 20, numFmt: isNumericFormat ? numFmt : undefined },
    ]);
};

const createSectionHeader = (label: string): ITableData => ({
    rowCells: [
        { value: label, cellFont: { bold: true }, width: 30 },
        { value: '' },
        { value: '' },
        { value: '' },
    ],
    rowFill: {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: EXCEL_COLORS.DARK_GRAY },
    },
});

export const formatDispositionSheet = ({
    dealName,
    disposition,
    saleDate,
    saleMonth,
    proFormaModelId,
    showCents = false,
    showPerGrossSf = false,
    showPerRentableSf = false,
    showPerUnit = false,
}: IDispositionSheetParams): IExcelReportData => {
    const config = getModelExportConfig(proFormaModelId);
    const { dispositionTab } = config;

    const today = new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });

    const topHeaderRows: ITableData[] = [
        createDataRow([{ value: dealName, cellFont: { bold: true, size: 14 }, width: 30 }]),
        createDataRow([{ value: 'Disposition / Exit Analysis', cellFont: { bold: true, size: 14 }, width: 30 }]),
        createDataRow([{ value: today, cellFont: { bold: true, size: 14 }, width: 30 }]),
        createBlankRow(4),
    ];

    const capRate = disposition?.marketCapitalizationRate?.current;

    const assumptionsRows: ITableData[] = [
        createDispositionLabelValueRow(dispositionTab.assumptions.saleDate, formatDate(saleDate, true)),
        createDispositionLabelValueRow(dispositionTab.assumptions.sellingCosts, disposition?.sellingCostsPercent, EXCEL_NUM_FORMATS.PERCENT),
        createDispositionLabelValueRow(dispositionTab.assumptions.saleMonth, saleMonth, EXCEL_NUM_FORMATS.NUMBER),
        createDispositionLabelValueRow(dispositionTab.assumptions.marketCapRate, capRate, EXCEL_NUM_FORMATS.PERCENT),
        createBlankRow(4),
    ];

    const columnHeaderRow = createHeaderRow(['', 'Untrended', 'Trended', 'At Sale'], EXCEL_COLORS.BLUE_HEADER, true);

    const currencyFmt = showCents ? EXCEL_NUM_FORMATS.CURRENCY_CENTS : EXCEL_NUM_FORMATS.CURRENCY;

    const valuationRows: ITableData[] = [
        createSectionHeader(dispositionTab.stabilizedValuation.sectionHeader),
        createTripleValueRow(dispositionTab.stabilizedValuation.stabilizedValuation,
            disposition?.stabilizedValue?.untrended,
            disposition?.stabilizedValue?.trended,
            disposition?.stabilizedValue?.sale,
            currencyFmt),
    ];

    if (showPerGrossSf && dispositionTab.stabilizedValuation.perGrossSf) {
        valuationRows.push(createTripleValueRow(dispositionTab.stabilizedValuation.perGrossSf,
            disposition?.stabilizedValuePerGrossSf?.untrended,
            disposition?.stabilizedValuePerGrossSf?.trended,
            disposition?.stabilizedValuePerGrossSf?.sale,
            currencyFmt));
    }

    if (showPerRentableSf) {
        valuationRows.push(createTripleValueRow(dispositionTab.stabilizedValuation.perRentableSf,
            disposition?.stabilizedValuePerSf?.untrended,
            disposition?.stabilizedValuePerSf?.trended,
            disposition?.stabilizedValuePerSf?.sale,
            currencyFmt));
    }

    if (showPerUnit && dispositionTab.stabilizedValuation.perUnit) {
        valuationRows.push(createTripleValueRow(dispositionTab.stabilizedValuation.perUnit,
            disposition?.stabilizedValuePerUnit?.untrended,
            disposition?.stabilizedValuePerUnit?.trended,
            disposition?.stabilizedValuePerUnit?.sale,
            currencyFmt));
    }

    const grossSaleProceeds = disposition?.grossSaleProceeds?.amount ?? 0;
    const sellingCosts = disposition?.sellingCosts?.amount ? -Math.abs(disposition.sellingCosts.amount) : 0;
    const netSaleProceeds = disposition?.netSaleProceeds?.amount ?? 0;
    const loanPayoff = disposition?.loanPayoff?.amount ?? 0;
    const equityProceeds = disposition?.equityProceedsFromSale?.amount ?? 0;

    const saleProceedsRows: ITableData[] = [
        createBlankRow(4),
        createSectionHeader(dispositionTab.saleProceeds.sectionHeader),
        createDataRow([
            { value: dispositionTab.saleProceeds.grossSaleProceeds, width: 30 },
            { value: '', width: 15 },
            { value: '', width: 15 },
            {
                value: grossSaleProceeds,
                width: 15,
                alignment: { horizontal: 'right' },
                numFmt: currencyFmt,
            },
        ]),
        createDataRow([
            { value: dispositionTab.saleProceeds.sellingCosts, width: 30 },
            { value: '', width: 15 },
            { value: '', width: 15 },
            {
                value: sellingCosts,
                width: 15,
                alignment: { horizontal: 'right' },
                numFmt: currencyFmt,
            },
        ]),
        {
            ...createDataRow([
                { value: dispositionTab.saleProceeds.netSaleProceeds, width: 30, cellFont: { bold: true } },
                { value: '', width: 15 },
                { value: '', width: 15 },
                {
                    value: netSaleProceeds,
                    width: 15,
                    alignment: { horizontal: 'right' },
                    cellFont: { bold: true },
                    numFmt: currencyFmt,
                },
            ], true),
            rowFill: {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: EXCEL_COLORS.GRAY_HEADER },
            },
        },
        createDataRow([
            { value: dispositionTab.saleProceeds.loanPayoff, width: 30 },
            { value: '', width: 15 },
            { value: '', width: 15 },
            {
                value: loanPayoff,
                width: 15,
                alignment: { horizontal: 'right' },
                numFmt: currencyFmt,
            },
        ]),
        {
            ...createDataRow([
                { value: dispositionTab.saleProceeds.equityProceedsFromSale, width: 30, cellFont: { bold: true } },
                { value: '', width: 15 },
                { value: '', width: 15 },
                {
                    value: equityProceeds,
                    width: 15,
                    alignment: { horizontal: 'right' },
                    cellFont: { bold: true },
                    numFmt: currencyFmt,
                },
            ], true),
            rowFill: {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: EXCEL_COLORS.GRAY_HEADER },
            },
        },
    ];

    return {
        topHeaderRows,
        reportTables: [
            {
                tableHeaderRows: [],
                tableData: assumptionsRows,
            },
            {
                tableHeaderRows: [columnHeaderRow],
                tableData: [
                    ...valuationRows,
                    ...saleProceedsRows,
                ],
            },
        ],
    };
};
